import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Unified badge styling. The `lg` + `accent` combo mirrors the pill style used
// on the landing firm-detail pages (assets, payment methods). Semantic status
// variants cover dashboard phase/state indicators so each page no longer has
// to ship its own color map.
const badgeVariants = cva(
  "group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent font-medium whitespace-nowrap transition-colors focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary:
          "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        destructive:
          "bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 [a]:hover:bg-destructive/20",
        outline:
          "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        // Firm-detail pill style — accent hover for tag-like labels.
        accent:
          "cursor-default border-border text-foreground hover:border-accent/30 hover:bg-accent/10 hover:text-accent",
        ghost:
          "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
        // ─── Semantic status variants ───
        step1: "border-blue-500/20 bg-blue-500/10 text-blue-500",
        step2: "border-amber-500/20 bg-amber-500/10 text-amber-500",
        funded: "border-emerald-500/20 bg-emerald-500/10 text-emerald-500",
        active: "border-emerald-500/20 bg-emerald-500/10 text-emerald-500",
        passed: "border-blue-500/20 bg-blue-500/10 text-blue-500",
        breached: "border-destructive/20 bg-destructive/10 text-destructive",
        paused: "border-amber-500/20 bg-amber-500/10 text-amber-500",
        warning: "border-amber-500/20 bg-amber-500/10 text-amber-500",
      },
      size: {
        xs: "h-4 gap-0.5 px-1.5 py-0 text-[9px]",
        sm: "h-5 px-2 py-0 text-[10px]",
        default: "h-5 px-2 py-0.5 text-xs",
        lg: "h-auto px-3.5 py-1.5 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  size = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant, size }), className),
      },
      props
    ),
    render,
    state: {
      slot: "badge",
      variant,
      size,
    },
  })
}

export { Badge, badgeVariants }
